import {
  UTC,
  isNullOrUndefined,
  fromEnumerable,
  guid,
}                                    from 'rewire-common';
import {
  observable,
}                                    from 'rewire-core';
import {
  ITemplate,
  IGroup,
  ITimecard,
  IOccupation,
  IReason,
  getMultipleObject,
  getMultiple,
}                                    from '../DataTypes';
import { IRolledUpTestTimecard }     from './TimecardTestSummaryDailyRollupModel';

export interface IRollupReason {
  name:        string;
  units:       number;
  factor:      number;
  amount:      number;
  columnOrder: number;
}

export default class TimecardTestSummaryRolledUpTimecard implements IRolledUpTestTimecard {
  id:            string = guid();
  earnedDate:    UTC;
  group:         any;
  template:      any;
  start:         any;
  end:           any;
  units:         any;
  rate:          any;
  gross:         any;
  reason:        any;
  occupation:    any;
  accountCode:   any;
  otherPay:      any;
  reasons:       IRollupReason[];
  timecards:     ITimecard[];
  otherPayments: ITimecard[];

  static create(earnedDate: UTC, timecards: ITimecard[], otherPayments: ITimecard[]): IRolledUpTestTimecard {
    const rolledUpTestTimecard = observable(new TimecardTestSummaryRolledUpTimecard());
    rolledUpTestTimecard.initialize(earnedDate, timecards, otherPayments);
    return rolledUpTestTimecard;
  }

  getStart() { return fromEnumerable<UTC>(this.timecards.filter(t => this.isRelevantCategoryForStartEnd(t)).map(t => t.start!)).min(); }
  getEnd()   { return fromEnumerable<UTC>(this.timecards.filter(t => this.isRelevantCategoryForStartEnd(t)).map(t => t.end!)).max(); }

  getUnits() {
    return this.timecards.reduce((totalUnits, thisTimecard) => {
      if (isNullOrUndefined(thisTimecard.units))          return totalUnits;
      if (!this.isRelevantCategoryForUnits(thisTimecard)) return totalUnits;

      // we haven't calculated a value for units yet, so totalUnits === undefined
      // just return the current timecard's value for units
      if (isNullOrUndefined(totalUnits)) return thisTimecard.units;

      // at this point, totalUnits is a number
      // return the sum of this previous total and the current timecard's units
      return (totalUnits! + thisTimecard.units);
    }, undefined);
  }

  getRate() {
    return this.timecards.reduce((rate, thisTimecard) => {
      if (!this.shouldEvaluateRate(thisTimecard)) return rate;

      if (isNullOrUndefined(rate))    return thisTimecard.rate;
      if (rate !== thisTimecard.rate) return getMultiple();
      return rate;
    }, undefined);
  }

  canCalculateAmount(timecard: ITimecard): boolean {
    return ((timecard.units !== undefined) && (timecard.rate !== undefined) && (timecard.factor !== undefined));
  }

  getGross() {
    return this.timecards.reduce((totalAmount, thisTimecard) => {
      if (!this.canCalculateAmount(thisTimecard)) return totalAmount;

      const currentAmount = (thisTimecard.units! * thisTimecard.rate! * thisTimecard.factor!);
      return totalAmount + currentAmount;
    }, 0);
  }

  getReason() {
    return this.timecards.reduce((reason: IReason, thisTimecard) => {
      if (!this.shouldEvaluateReason(thisTimecard)) return reason;
      if (!(reason?.id))                            return thisTimecard.reason;
      if (!(thisTimecard.reason?.id))               return reason;
      if (reason.id! !== thisTimecard.reason.id)    return getMultipleObject();
      return reason;
    }, undefined);
  }

  getOccupation() {
    return this.timecards.reduce((occupation: IOccupation, thisTimecard) => {
      if (!(occupation?.id))                            return thisTimecard.occupation;
      if (!(thisTimecard.occupation?.id))               return occupation;
      if (occupation.id !== thisTimecard.occupation.id) return getMultipleObject();
      return occupation;
    }, undefined);
  }

  getAccountCode() {
    return this.timecards.reduce((accountCode, thisTimecard) => {
      if (isNullOrUndefined(accountCode))              return thisTimecard.accountCode;
      if (isNullOrUndefined(thisTimecard.accountCode)) return accountCode;
      if (accountCode !== thisTimecard.accountCode)    return getMultiple();
      return accountCode;
    }, undefined);
  }

  getOtherPay() {
    return this.otherPayments.reduce((totalAmount, thisPayment) => {
      if ((isNullOrUndefined(thisPayment.units)) || (isNullOrUndefined(thisPayment.rate)) || (isNullOrUndefined(thisPayment.factor))) return totalAmount;

      const currentAmount = (thisPayment.units! * thisPayment.rate! * thisPayment.factor!);
      return totalAmount + currentAmount;
    }, 0);
  }

  getTemplate() {
    return this.timecards.reduce((template: ITemplate | undefined, thisTimecard) => {
      const t             = template;
      const thisTimecardT = thisTimecard.template;

      if (!(template?.id))                          return thisTimecardT;
      if (!(thisTimecard.template?.id))             return t;
      if (template.id !== thisTimecard.template.id) return getMultipleObject();
      return t;
    }, undefined);
  }

  getGroup() {
    return this.timecards.reduce((group: IGroup | undefined, thisTimecard) => {
      const t             = group;
      const thisTimecardT = thisTimecard.group;

      if (!(group?.id))                       return thisTimecardT;
      if (!(thisTimecard.group?.id))          return t;
      if (group.id !== thisTimecard.group.id) return getMultipleObject();
      return t;
    }, undefined);
  }

  private initialize(earnedDate: UTC, timecards: ITimecard[], otherPayments: ITimecard[]) {
    this.earnedDate     = earnedDate;
    this.reasons        = [];
    this.timecards      = timecards;
    this.otherPayments  = otherPayments;
    this.processReasons();

    this.group       = this.getGroup      ();
    this.template    = this.getTemplate   ();
    this.start       = this.getStart      ();
    this.end         = this.getEnd        ();
    this.units       = this.getUnits      ();
    this.rate        = this.getRate       ();
    this.gross       = this.getGross      ();
    this.reason      = this.getReason     ();
    this.occupation  = this.getOccupation ();
    this.accountCode = this.getAccountCode();
    this.otherPay    = this.getOtherPay   ();

    this.sortTimecards(this.timecards);
  }

  private shouldEvaluateRate(timecard: ITimecard): boolean {
    if (!timecard.reason || !timecard.reason.category)               return false;
    if (['premium', 'break'].includes(timecard.reason.category))     return false;

    // worked, absence
    return true;
  }

  private shouldEvaluateReason(timecard: ITimecard): boolean {
    if (!timecard.reason || !timecard.reason.category)               return false;
    if (['premium', 'break'].includes(timecard.reason.category))     return false;

    return true;
  }

  private shouldProcessReason(timecard: ITimecard): boolean {
    return (!!timecard.reason && (typeof timecard.factor === 'number'));
    // return (!!timecard.reason && !!timecard.factor && (timecard.factor > 0));
  }

  private processReasons() {
    this.reasons.length = 0;
    for (const timecard of this.timecards) {
      if (!this.shouldProcessReason(timecard)) {
        continue;
      }

      const key:         string = this.makeReasonKey(timecard);
      const columnOrder: number = this.determineSortOrder(timecard);
      let reason                = this.reasons.find(r => r.name === key && r.columnOrder === columnOrder);
      if (!reason) {
        reason = {
          name:        key,
          columnOrder,
          factor:      timecard.factor!,
          units:       0,
          amount:      0
        };
        this.reasons.push(reason);
      }
      reason.units  += timecard.units || 0;
      reason.amount += (timecard.units! * timecard.rate! * timecard.factor!);
    }
  }

  private makeReasonKey(timecard: ITimecard): string {
    return `${timecard.reason!.name} ${timecard.factor!.toFixed(1)}x`;
  }

  private isRelevantCategoryForUnits(timecard: ITimecard): boolean {
    return timecard.reason?.category && ['worked', 'overtime', 'absence'].includes(timecard.reason!.category!) || false;
  }

  private isRelevantCategoryForStartEnd(timecard: ITimecard): boolean {
    return timecard.reason?.category && ['worked', 'overtime', 'break', 'absence'].includes(timecard.reason!.category!) || false;
  }

  sortTimecards(timecards: ITimecard[]) {
    timecards.sort((t1, t2) => {
      if (t1.earnedDate.utc !== t2.earnedDate.utc) return t1.earnedDate.utc - t2.earnedDate.utc;

      const rank1     = t1.start ? t1.start.utc : Number.POSITIVE_INFINITY;
      const rank2     = t2.start ? t2.start.utc : Number.POSITIVE_INFINITY;
      const sortOrder = this.determineSortOrder(t1) - this.determineSortOrder(t2);

      if (rank1 !== rank2) return (rank1 - rank2);
      if (sortOrder !== 0) return sortOrder;

      // start values not present
      // if end values are present, use them as a secondary sort
      return (t1.end && t2.end)
        ? (t1.end.utc - t2.end.utc)
        // otherwise, consider the entries equal (for sorting purposes)
        : 0;
    });
  }

  determineSortOrder(timecard: ITimecard): number {
    const category = timecard.reason?.category;
    if (!category) return 6;

    switch (category) {
      case 'worked':
        return timecard.overtimeReason ? 2 : 1;

      case 'premium':
        return timecard.overtimeReason ? 4 : 3;

      case 'absence':
        return 5;

      default:
        return 7;
    }
  }
}